import { ActionName, WorkflowScopeType } from '@invenco/common-domain/enums';
import { ActionDTO } from '@invenco/common-interface/automation';

import { type EditorAction, type EditorActionBranch } from '../editor-model';
import { isValuePresent, type FieldInputValue } from '../field-input';
import { getActionDefinition, getScopeActions, isActionDisabled } from '../field-registry';

/**
 * Pure transforms over a rule's actions, staged through `editor.updateAction`.
 * `ActionDTO.params` is `Record<string, any>`; the panel works in the
 * descriptor-typed `FieldInputValue` and converts here.
 */

/** The panel's per-param value map — the typed view of `ActionDTO.params`. */
export type ActionParams = Record<string, FieldInputValue>;

export const actionParams = (action: EditorAction): ActionParams =>
  (action.params ?? {}) as ActionParams;

/** The enabled action types in a scope, in backend-declared order. */
export const enabledActionTypes = (scope: WorkflowScopeType): ActionName[] =>
  getScopeActions(scope).filter((type) => !isActionDisabled(type));

/** A new action: typeless scaffolding until the user picks a type, born `fresh`. */
export const newAction = (): EditorAction => ({
  params: {},
  fresh: true,
});

/** Sets a param, dropping keys cleared to empty so blanks are omitted from the payload. */
export const setActionParam = (
  action: EditorAction,
  key: string,
  value: FieldInputValue,
): EditorAction => {
  const next: ActionParams = { ...actionParams(action) };
  if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return { ...action, params: next };
};

/**
 * Setting the type keeps the whole param bag, so switching back (or to a type
 * sharing a key) restores what was typed; denormalize trims undeclared keys
 * from save payloads. The grace period restarts as if freshly added.
 */
export const setActionType = (action: EditorAction, type: ActionName): EditorAction => ({
  ...action,
  type,
  fresh: true,
});

export const appendAction = <T extends { actions: EditorAction[]; elseActions: EditorAction[] }>(
  rule: T,
  branch: EditorActionBranch,
): T => ({ ...rule, [branch]: [...rule[branch], newAction()] });

/** Ends every action's grace period — a failed submit reveals missing params. */
export const revealActions = <T extends { actions: EditorAction[]; elseActions: EditorAction[] }>(
  rule: T,
): T => ({
  ...rule,
  actions: rule.actions.map((action) => ({ ...action, fresh: false })),
  elseActions: rule.elseActions.map((action) => ({ ...action, fresh: false })),
});

export const removeAction = <T extends { actions: EditorAction[]; elseActions: EditorAction[] }>(
  rule: T,
  branch: EditorActionBranch,
  index: number,
): T => ({ ...rule, [branch]: rule[branch].filter((_, i) => i !== index) });

/** Every required param on the action's type carries a value. */
export const isActionComplete = (action: ActionDTO): boolean => {
  const params = actionParams(action);
  return getActionDefinition(action.type).params.every(
    (param) => !param.required || isValuePresent(params[param.key]),
  );
};
